
import React from 'react'
import { Grid, Typography } from '@mui/material'
import { withComponentContext } from 'widgets-shared/core'
import { compose } from 'widgets-shared/core'
import { homeFirstCompetitions } from 'smartstream-widgets/constants/shared'
import FixtureHeader from 'smartstream-widgets/components/FixtureHeader/FixtureHeader'
import { withTranslations } from 'widgets-shared/core'
import useMatchedBreakpointValues from 'smartstream-widgets/hooks/useMatchedBreakpointValues/useMatchedBreakpointValues'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { Breakpoint } from '@mui/system'
import { TranslationFunction } from 'smartstream-widgets/types/sharedModule/core'
import SingleTeamNameAndScore from 'smartstream-widgets/controls/SingleTeamNameAndScore/SingleTeamNameAndScore'
import { Fixture, PostMatchCompetitors } from './types'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'

type Props = {
	classes: {
		[K in string]: string
	},
	className: string,
	containerRef: React.RefObject<HTMLDivElement>,
	data: Fixture,
	showNames: boolean,
	theme: Theme,
	translations: {
		[K in string]: string
	}
}

type LogoFlagOverrides = {
	logoSize: number
}

const styles = (theme: Theme) => createStyles({
	affiliateText: {
		color: theme.palette.text.secondary
	},
	caption: {
		color: theme.palette.grey['700'],
		fontSize: 9,
		padding: `0px ${theme.spacing(1)}`
	},
	captionOdd: {},
	container: {
		height: '100%',
		minHeight: 250,
		'&::after': {
			content: '\'\'',
			display: 'block',
			height: 30
		}
	},
	cta: {
		margin: `${theme.spacing(1)} auto`,
		minHeight: 33
	},
	header: {
		height: 'auto'
	},
	oddButtonDisabled: {},
	odds: {
		justifyContent: 'space-around',
		padding: `${theme.spacing(1)} ${theme.spacing(3)}`
	},
	scoresContainer: {
		height: 100,
		padding: `10px ${theme.spacing(3)}`,
		'& *:last-child': {
			order: 2
		}
	},
	scoreDivider: {
		fontSize: '10px',
		fontWeight: 700,
		textAlign: 'center',
		lineHeight: 4,
		color: theme.palette.text.primary,
		'&::before': {
			background: theme.palette.divider,
			content: '\'\'',
			display: 'block',
			margin: `-5px 20px`,
			height: '36px',
			width: '1px'
		},
		'&::after': {
			background: theme.palette.divider,
			content: '\'\'',
			display: 'block',
			margin: `-5px 20px`,
			height: '36px',
			width: '1px'
		}
	},
	venueCaption: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		transform: 'translateY(4px)',
		color: theme.palette.grey['600'],
		fontSize: 10,
		'& svg': {
			display: 'flex',
			width: '12px',
			height: '12px',
			marginRight: 4
		}
	},
	scorePredicted: {
		fontSize: 44
	},
	scoreLabel: {
		fontSize: 9
	},
	teamContent: {
		maxWidth: 150
	},
	teamNames: {
		fontSize: 14
	},
	title: {
		fontSize: 16
	},
	logoFlagSize: {
		height: 48,
		width: 100
	},
	teamLogoSize: {
		width: 40,
		height: 40
	}
})

export function findCompetitors(data: Fixture): PostMatchCompetitors {
	const competitors = data.competitors
	const hasCurrentScores = data.matchSummaries &&
		Number.isInteger(data.matchSummaries.homeScore) &&
		Number.isInteger(data.matchSummaries.awayScore)

	return competitors.reduce((result, competitor, index) => {
		const homeOrAway = competitor.homeOrAway || (index ? 'AWAY' : 'HOME')
		const competitorCurrentScore = hasCurrentScores && data.matchSummaries[homeOrAway === 'HOME' ? 'homeScore' : 'awayScore']
		result[homeOrAway] = {
			...competitor,
			currentScore: competitorCurrentScore || 0
		}
		return result
	}, {})
}

export function PostMatchComponent(props: Props) {
	const {
		classes,
		className = '',
		containerRef,
		data,
		showNames = false,
		theme,
		translations
	} = props
	const competitors = findCompetitors(data)
	const isAwayTeamFirst = !homeFirstCompetitions.includes(data.competitionId)
	const isAwayWinning = competitors.AWAY.currentScore >= competitors.HOME.currentScore
	const isHomeWinning = competitors.HOME.currentScore >= competitors.AWAY.currentScore
	const headerInfo = {
		helperInfo: {
			title: translations.aboutWidgetTitle,
			content: translations.helperInfoContent,
			disclaimer: translations.helperInfoDisclaimer,
			footerText: translations.poweredByFooterText
		}
	}

	const logoFlagOverrides: { [size in Breakpoint]?: LogoFlagOverrides } = {
		'c728': {
			logoSize: 48
		},
		'c576': {
			logoSize: 46
		},
		'c420': {
			logoSize: 40
		}
	}

	const singlePredictedScoreClasses = {
		header: classes.teamNames,
		scorePredicted: classes.scorePredicted,
		content: classes.teamContent
	}
	const styleOverrides = useMatchedBreakpointValues(logoFlagOverrides, theme)

	let matchState: { scoreAway: number, scoreHome: number, phase: string, matchStatus: string }

	if (data.matchSummaries) {
		matchState = {
			scoreAway: data.matchSummaries.awayScore,
			scoreHome: data.matchSummaries.homeScore,
			phase: data.matchSummaries.phase,
			matchStatus: data.matchSummaries.matchStatus
		}
	}

	return (
		<>
			<Grid container direction='column' className={`${classes.container} ${className}`} justifyContent='space-between' wrap='nowrap' data-testid="post-match-component">
				<FixtureHeader
					logoFlagOverrides={styleOverrides}
					classes={{
						content: classes.header,
						matchState: classes.matchState,
						title: classes.title,
						logoFlagSize: classes.logoFlagSize,
						venueCaption: classes.venueCaption
					}}
					competitors={data.competitors}
					containerRef={containerRef}
					headerInfo={headerInfo}
					isAwayTeamFirst={isAwayTeamFirst}
					matchState={matchState}
					scheduledStartTime={data.scheduledStartTime}
					showNames={showNames}
					venueName={data.venueName}
					hideLiveScore
				/>
				<Grid container className={classes.scoresContainer} justifyContent='space-around' wrap='nowrap'>
					<SingleTeamNameAndScore
						classes={singlePredictedScoreClasses}
						competitor={isAwayTeamFirst ? competitors.AWAY : competitors.HOME}
						winning={isAwayTeamFirst ? isAwayWinning : isHomeWinning}
					/>
					<Typography className={classes.scoreDivider}>
						{translations.scoreDividerContent}
					</Typography>
					<SingleTeamNameAndScore
						classes={singlePredictedScoreClasses}
						competitor={isAwayTeamFirst ? competitors.HOME : competitors.AWAY}
						winning={isAwayTeamFirst ? isHomeWinning : isAwayWinning}
					/>
				</Grid>
			</Grid>
		</>
	)
}

export default compose(
	withComponentContext,
	withStyles(styles, { name: 'PostMatchComponent', withTheme: true, defaultTheme: getDefaultTheme }),
	withTranslations((t: TranslationFunction) => {
		return {
			aboutWidgetTitle: t('about-title.translation', {
				defaultValue: 'About Predicted Score',
				context: 'Text to display as title in the Predicted Score info popup'
			}),
			helperInfoContent: t('about-info.translation', {
				defaultValue: 'Bringing you the best in pre-match and inplay sports data, our Predicted Score widget gives you the info you need to fuel your hobby',
				context: 'Text to display as content inside info popup inside Predicted Score widget'
			}),
			scoreDividerContent: t('postmatch-component.translation', {
				defaultValue: 'Final',
				context: 'Text to display as content divider for scores'
			})
		}
	})
)(PostMatchComponent)
